import { useReducer, useCallback, useMemo } from 'react';

const initialState = {
    isSelectionMode: false,
    selectedLogIds: new Set(),
    activeInfoLogId: null,
    isConfirmDeleteOpen: false,
};

export function callLogReducer(state, action) {
    switch (action.type) {
        case 'TOGGLE_SELECTION_MODE':
            return {
                ...state,
                isSelectionMode: !state.isSelectionMode,
                selectedLogIds: new Set(),
                activeInfoLogId: null, // close info panel when selecting
            };
        case 'SET_SELECTION_MODE':
            return {
                ...state,
                isSelectionMode: action.payload,
                selectedLogIds: action.payload ? state.selectedLogIds : new Set(),
            };
        case 'SELECT_ALL':
            return { ...state, selectedLogIds: new Set(action.payload) };
        case 'DESELECT_ALL':
            return { ...state, selectedLogIds: new Set() };
        case 'TOGGLE_LOG_SELECTION': {
            const newSelected = new Set(state.selectedLogIds);
            if (newSelected.has(action.payload)) {
                newSelected.delete(action.payload);
            } else {
                newSelected.add(action.payload);
            }
            return { ...state, selectedLogIds: newSelected };
        }
        case 'OPEN_INFO_PANEL':
            return { ...state, activeInfoLogId: action.payload, isSelectionMode: false };
        case 'CLOSE_INFO_PANEL':
            return { ...state, activeInfoLogId: null };
        case 'SET_CONFIRM_DELETE':
            return { ...state, isConfirmDeleteOpen: action.payload };
        default:
            return state;
    }
}

export const useCallLogViewModel = logsData => {
    const [state, dispatch] = useReducer(callLogReducer, initialState);

    const totalLogsCount = useMemo(() => {
        return logsData.reduce((acc, curr) => acc + curr.logs.length, 0);
    }, [logsData]);

    const allLogIds = useMemo(() => {
        return logsData.flatMap(group => group.logs.map(log => log.id));
    }, [logsData]);

    const handleToggleSelectionMode = useCallback(() => {
        dispatch({ type: 'TOGGLE_SELECTION_MODE' });
    }, []);

    const handleSelectAllToggle = useCallback(() => {
        if (state.selectedLogIds.size === totalLogsCount) {
            dispatch({ type: 'DESELECT_ALL' });
        } else {
            dispatch({ type: 'SELECT_ALL', payload: allLogIds });
        }
    }, [state.selectedLogIds.size, totalLogsCount, allLogIds]);

    const handleToggleLogSelection = useCallback(id => {
        dispatch({ type: 'TOGGLE_LOG_SELECTION', payload: id });
    }, []);

    const handleOpenInfoPanel = useCallback(id => {
        dispatch({ type: 'OPEN_INFO_PANEL', payload: id });
    }, []);

    const handleCloseInfoPanel = useCallback(() => {
        dispatch({ type: 'CLOSE_INFO_PANEL' });
    }, []);

    const handleDeleteLogs = useCallback(() => {
        dispatch({ type: 'SET_CONFIRM_DELETE', payload: true });
    }, []);

    const confirmDelete = useCallback(
        deleteCallback => {
            if (deleteCallback) {
                deleteCallback(Array.from(state.selectedLogIds));
            }
            dispatch({ type: 'SET_SELECTION_MODE', payload: false });
            dispatch({ type: 'SET_CONFIRM_DELETE', payload: false });
        },
        [state.selectedLogIds],
    );

    const cancelDelete = useCallback(() => {
        dispatch({ type: 'SET_CONFIRM_DELETE', payload: false });
    }, []);

    return {
        ...state,
        totalLogsCount,
        handleToggleSelectionMode,
        handleSelectAllToggle,
        handleToggleLogSelection,
        handleOpenInfoPanel,
        handleCloseInfoPanel,
        handleDeleteLogs,
        confirmDelete,
        cancelDelete,
    };
};
